<template>
  <Transition name="fade">
    <button
      v-if="visible"
      class="scroll-top-btn"
      @click="scrollToTop"
      title="맨 위로"
    >
      <Icon name="heroicons:chevron-up" size="20" />
    </button>
  </Transition>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  // 스크롤 대상 요소 (기본: window)
  target: {
    type: [Object, String],
    default: null,
  },
  // 버튼이 나타나는 스크롤 위치 (px)
  threshold: {
    type: Number,
    default: 200,
  },
});

const visible = ref(false);
let scrollTarget = null;

// 스크롤 이벤트 핸들러
function handleScroll() {
  if (scrollTarget) {
    const scrollTop = scrollTarget === window
      ? window.scrollY || document.documentElement.scrollTop
      : scrollTarget.scrollTop;
    visible.value = scrollTop > props.threshold;
  }
}

// 맨 위로 스크롤
function scrollToTop() {
  if (scrollTarget) {
    if (scrollTarget === window) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      scrollTarget.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}

// 스크롤 대상 설정
function setScrollTarget() {
  if (props.target) {
    if (typeof props.target === 'string') {
      scrollTarget = document.querySelector(props.target);
    } else {
      scrollTarget = props.target;
    }
  } else {
    scrollTarget = window;
  }
}

// target prop 변경 감지 (ref 요소가 나중에 할당되는 경우 대응)
watch(
  () => props.target,
  (newTarget) => {
    // 기존 리스너 제거
    if (scrollTarget) {
      scrollTarget.removeEventListener('scroll', handleScroll);
    }
    // 새 타겟 설정
    setScrollTarget();
    if (scrollTarget) {
      scrollTarget.addEventListener('scroll', handleScroll);
      handleScroll();
    }
  },
);

onMounted(() => {
  setScrollTarget();
  if (scrollTarget) {
    scrollTarget.addEventListener('scroll', handleScroll);
    // 초기 상태 체크
    handleScroll();
  }
});

onUnmounted(() => {
  if (scrollTarget) {
    scrollTarget.removeEventListener('scroll', handleScroll);
  }
});
</script>

<style lang="scss" scoped>
.scroll-top-btn {
  position: fixed;
  bottom: 30px;
  right: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #333;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  z-index: 999;

  &:hover {
    background-color: #222;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
}

// 페이드 애니메이션
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
